import { html, nothing, svg, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { timezoneGroups } from "./timezones";
import { createActorState, type ActorStateOf } from "../state/actor-state";
import { LitActorController } from "../state/lit-actor-controller";
import { createDiscoveryResourceMachine } from "../state/discovery/resource.machine";
import { ZipperEmbedElement } from "./base/zipper-embed-element";
import { appointmentsStyles } from "./appointments/appointments.styles";
import type {
  AppointmentOptions,
  ComponentType,
  EmbedConfig,
} from "../config/types";
import { DEFAULT_THEME } from "../config/types";
import {
  MOCK_PROVIDERS,
  MOCK_SERVICES,
  addDays,
  firstAvailableDate,
  slotsForProviderDay,
  totalSlots,
  type AppointmentGroupTag,
  type AppointmentProvider,
  type AppointmentService,
  type DaySlots,
} from "./data/mock-appointments";
// PLAT-5624: appointment-type GROUPS. The merge itself is a pure module so it
// can be unit-tested without mounting the widget.
import {
  buildAppointmentGroups,
  mergedDurationLabel,
  mergedImage,
  mergedPriceLabel,
  type AppointmentGroup,
  type AppointmentGroupProvider,
  type AppointmentGroupsResult,
} from "./appointment-groups";
import { serviceForms } from "./appointment-forms";
import {
  fetchAppointmentData,
  fetchAppointmentAvailabilityDays,
  fetchMemberContext,
  formatOrgLocalTime,
  orgAppointmentDayKey,
  orgLocalHour,
  orgWallDayKey,
} from "../data/live";
import { clearEmbedToken } from "../config/client";
import { openSignIn } from "./internal/open-signin";
import {
  createAppointment,
  type AppointmentTimeslot,
} from "../payments/stripe-client";
// AP-CO: the appointment checkout now renders the shared two-column checkout
// (same component the class flow uses) for parity, instead of the widget's own
// thin confirm card + standalone payment/confirmation.
import "./internal/zipper-booking-checkout";
import "./internal/zipper-commerce-checkout";
import { runtimeApiArguments } from "../runtime/context";
import { renderAppointmentService } from "./appointments/appointment-service.view";
import {
  appointmentServiceOfferingKey,
  groupSharedAppointmentServices,
  sharedAppointmentProviderIds,
  type SharedAppointmentDisplayOption,
} from "./shared-appointment-options";
import {
  CommerceOfferingKind,
  CommerceRequestedAction,
  type CommerceAppointmentOfferingRefDto,
  type CommerceAppointmentTimeslotSelectionDto,
} from "@zipper/customer-commerce-client";
import { formatMinorAmount, formatShortAmount } from "./format-money";

const EMPTY_DAY: DaySlots = { morning: [], afternoon: [], evening: [] };

/**
 * PLAT-6790: availability loads a week of per-day COUNTS at a time (enough to
 * light up the date strip), and the actual times only for the day selected.
 * Shipping every slot for 30 days was ~0.5 MB for a busy provider. The
 * first-available search walks forward week by week (up to
 * {@link APPT_FIRST_AVAILABLE_DAYS}); other weeks load when navigated to.
 */
export const APPT_WINDOW_DAYS = 7;
export const APPT_FIRST_AVAILABLE_DAYS = 30;
export const APPT_HORIZON_DAYS = 90;

type AppointmentAvailabilityQuery = {
  enabled: boolean;
  orgId: number;
  providerUserId: number;
  serviceId: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  /** Org-local `YYYY-MM-DD` whose slots to return; "" = counts only. */
  day: string;
};

type AppointmentAvailabilityResult = Awaited<
  ReturnType<typeof fetchAppointmentAvailabilityDays>
>;

const disabledAvailabilityQuery = (): AppointmentAvailabilityQuery => ({
  enabled: false,
  orgId: 0,
  providerUserId: 0,
  serviceId: "",
  startsAt: "",
  endsAt: "",
  timezone: "UTC",
  day: "",
});

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ICON_BACK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>`;
const ICON_CHEVRON_L = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
const ICON_CHEVRON_R = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;
const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_SIGNIN = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;
const ICON_CLOCK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
// Non-numeric placeholder for a service card with no thumbnail image (AP2):
// a generic photo glyph, never a stray character from the service name.
const ICON_IMAGE = svg`<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.6"/><path d="M21 15l-5-5L5 21"/></svg>`;

const appointmentsActorState = createActorState(() => ({
  step: 1,
  expandedServiceIds: new Set<string>(),
  erroredThumbIds: new Set<string>(),
  loadedThumbIds: new Set<string>(),
  collapsedCats: new Set<string>(),
  services: [] as AppointmentService[],
  providers: [] as AppointmentProvider[],
  servicesLoading: true,
  serviceId: "",
  serviceOfferingKey: "",
  providerId: "any",
  groupTitle: "",
  groupProviderId: "",
  sharedChoiceId: "",
  sharedChoiceProviderId: "",
  orgGroups: [] as AppointmentGroupTag[],
  date: new Date(),
  time: "",
  weekStart: new Date(),
  live: false,
  liveAvail: {} as Record<string, DaySlots>,
  orgTimezone: "UTC",
  selectedTz: "",
  signedInName: "",
  appointmentBookError: "",
  bookingAction: CommerceRequestedAction.Book as CommerceRequestedAction,
}));

type AppointmentsActorState = ActorStateOf<typeof appointmentsActorState>;

/**
 * <zipper-appointments instance="..."> — book a service appointment.
 * Ported from the design-handoff `appointments.jsx`: 3 steps — choose a service
 * → pick instructor/date/time → confirm + pay. Add-ons are a follow-up. Mock
 * data until the live appointment endpoints are wired; charges via the shared
 * <zipper-payment> and ends in <zipper-confirmation kind="appointment">.
 */
@customElement("zipper-appointments")
export class ZipperAppointments extends ZipperEmbedElement {
  static styles = appointmentsStyles;

  @appointmentsActorState.field("step")
  private step!: AppointmentsActorState["step"];

  /** Service ids whose description accordion is open (APT-35). */
  @appointmentsActorState.field("expandedServiceIds")
  private expandedServiceIds!: AppointmentsActorState["expandedServiceIds"];

  /** AP2: service ids whose thumbnail URL failed to load (404/broken) and
   * never painted; those fall back to the photo-glyph placeholder instead of a
   * broken-image icon. */
  @appointmentsActorState.field("erroredThumbIds")
  private erroredThumbIds!: AppointmentsActorState["erroredThumbIds"];

  /** AP-IMG: service ids whose thumbnail has successfully painted at least once
   * (`@load` fired). Once loaded, the image NEVER falls back to the glyph, even
   * if a spurious `error` fires while the <img> is re-evaluated on an unrelated
   * re-render (accordion expand, provider/tz state). */
  @appointmentsActorState.field("loadedThumbIds")
  private loadedThumbIds!: AppointmentsActorState["loadedThumbIds"];

  /** Category section titles that are collapsed (APT-34); empty = all open. */
  @appointmentsActorState.field("collapsedCats")
  private collapsedCats!: AppointmentsActorState["collapsedCats"];
  // AP-LOAD: start EMPTY on the live path and hold a loading skeleton until
  // `fetchAppointmentData` resolves — so no sample/"design example" services
  // flash before the real ones. MOCK_SERVICES/MOCK_PROVIDERS are seeded only on
  // the mock/playground path (fetch fails or the org has no services).
  @appointmentsActorState.field("services")
  private services!: AppointmentsActorState["services"];

  @appointmentsActorState.field("providers")
  private providers!: AppointmentsActorState["providers"];

  /** True until the first appointment-data fetch settles (skeleton gate). */
  @appointmentsActorState.field("servicesLoading")
  private servicesLoading!: AppointmentsActorState["servicesLoading"];

  @appointmentsActorState.field("serviceId")
  private serviceId!: AppointmentsActorState["serviceId"];

  @appointmentsActorState.field("serviceOfferingKey")
  private serviceOfferingKey!: AppointmentsActorState["serviceOfferingKey"];

  @appointmentsActorState.field("providerId")
  private providerId!: AppointmentsActorState["providerId"];
  /**
   * PLAT-5624: the appointment-type GROUP the guest drilled into. This is a
   * SUB-step of step 1 — `step` stays 1 so the 1/2/3 crumbs (and the `.crumbs`
   * presentation lock) are untouched. Held by TITLE, never id: the dashboard
   * mints a fresh group uuid on every provider's copy of a type.
   * Empty = the top-level list (groups + ungrouped services).
   */
  @appointmentsActorState.field("groupTitle")
  private groupTitle!: AppointmentsActorState["groupTitle"];

  /** The provider chosen inside `groupTitle` (stringified user id). Empty =
   * the provider list for that group. */
  @appointmentsActorState.field("groupProviderId")
  private groupProviderId!: AppointmentsActorState["groupProviderId"];

  @appointmentsActorState.field("sharedChoiceId")
  private sharedChoiceId!: AppointmentsActorState["sharedChoiceId"];

  @appointmentsActorState.field("sharedChoiceProviderId")
  private sharedChoiceProviderId!: AppointmentsActorState["sharedChoiceProviderId"];

  /** The org's mirrored group roster — ORDERING metadata only, never an
   * allow-list (see `AppointmentData.orgGroups`). */
  @appointmentsActorState.field("orgGroups")
  private orgGroups!: AppointmentsActorState["orgGroups"];

  @appointmentsActorState.field("date")
  private date!: AppointmentsActorState["date"];

  @appointmentsActorState.field("time")
  private time!: AppointmentsActorState["time"];

  @appointmentsActorState.field("weekStart")
  private weekStart!: AppointmentsActorState["weekStart"];

  /** True once real schedule data loaded — switches slots from mock to live. */
  @appointmentsActorState.field("live")
  private live!: AppointmentsActorState["live"];

  /** Live availability for the selected service+provider, keyed by org-local day. */
  @appointmentsActorState.field("liveAvail")
  private liveAvail!: AppointmentsActorState["liveAvail"];

  private readonly availability = new LitActorController(
    this,
    createDiscoveryResourceMachine<
      AppointmentAvailabilityQuery,
      AppointmentAvailabilityResult
    >({
      key: (query) => JSON.stringify(query),
      load: async ({ query, signal }) => {
        if (!query.enabled) return [];
        const result = await fetchAppointmentAvailabilityDays(
          query.orgId,
          query.providerUserId,
          query.serviceId,
          query.startsAt,
          query.endsAt,
          query.timezone,
          query.day || undefined,
        );
        signal.throwIfAborted();
        return [result];
      },
      onResolved: ({ query, items }) => {
        this.applyAvailability(query, items[0] ?? null);
      },
      onRejected: ({ query }) => {
        this.applyAvailability(query, null);
      },
    }),
    { input: { query: disabledAvailabilityQuery() } },
  );

  private get availLoading(): boolean {
    return (
      this.availability.snapshot.context.query.enabled &&
      this.availability.snapshot.matches("loading")
    );
  }
  /** Org IANA timezone for slot labels and day grouping. */
  @appointmentsActorState.field("orgTimezone")
  private orgTimezone!: AppointmentsActorState["orgTimezone"];

  @appointmentsActorState.field("selectedTz")
  private selectedTz!: AppointmentsActorState["selectedTz"];

  @appointmentsActorState.field("signedInName")
  private signedInName!: AppointmentsActorState["signedInName"];

  @appointmentsActorState.field("appointmentBookError")
  private appointmentBookError!: AppointmentsActorState["appointmentBookError"];

  @appointmentsActorState.field("bookingAction")
  private bookingAction!: AppointmentsActorState["bookingAction"];
  /** Raw live slots (to submit the exact {id,startTime,endTime} on booking). */
  private liveRawSlots: AppointmentTimeslot[] = [];
  /** PLAT-6790: open-slot count per org-local day, for the date strip. */
  private dayCounts: Record<string, number> = {};
  /** Org-local days whose slots are in `liveRawSlots`. */
  private loadedDays = new Set<string>();
  /** PLAT-6790: day 0 of the current selection's availability windows. */
  private availOrigin: Date | null = null;
  /** Window indexes loaded (or failed) for the current selection. */
  private availWindowsDone = new Set<number>();
  /** Still walking forward to the first day with openings. */
  private availSearching = false;
  /** Base query (minus dates) for the current selection. */
  private availBase: Omit<
    AppointmentAvailabilityQuery,
    "startsAt" | "endsAt" | "day"
  > | null = null;
  /** appointmentTypeId → providerUserIds offering it (live). */
  private providersByService: Record<string, number[]> = {};
  /** The slot the guest picked (live), for the create call. */
  private selectedSlot: AppointmentTimeslot | null = null;
  private loadedAuthorityKey = "";

  protected get expectedType(): ComponentType {
    return "appointment";
  }
  private get opts(): AppointmentOptions {
    return (this.config?.options as AppointmentOptions) ?? {};
  }
  private get accent(): string {
    return this.config?.theme?.accent ?? DEFAULT_THEME.accent;
  }
  /**
   * AP-RECIP: hide the "who are you booking for?" family/friend panel. This is
   * a SHARED embed option (already on Schedule/Shop options + the API
   * EmbedOptionsDto, settings label "Hide family/friend booking"); it isn't yet
   * declared on `AppointmentOptions`, so it's read via a narrow cast rather than
   * editing the shared config types. Default false → the panel shows.
   */
  private get hideRecipientBooking(): boolean {
    return !!this.opts.hideRecipientBooking;
  }
  private get service(): AppointmentService | undefined {
    const providerId =
      this.providerId && this.providerId !== "any"
        ? Number(this.providerId)
        : null;
    return (
      this.services.find(
        (service) =>
          appointmentServiceOfferingKey(service) === this.serviceOfferingKey,
      ) ??
      (providerId != null
        ? this.services.find(
            (s) => s.id === this.serviceId && s.providerUserId === providerId,
          )
        : undefined) ??
      this.services.find((s) => s.id === this.serviceId)
    );
  }
  private get provider(): AppointmentProvider | undefined {
    return this.providers.find((p) => p.id === this.providerId);
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if ((changed.has("config") || changed.has("runtime")) && this.config) {
      const authorityKey = this.discoveryAuthorityKey();
      if (authorityKey === this.loadedAuthorityKey) return;
      this.loadedAuthorityKey = authorityKey;
      this.step = 1;
      this.services = [];
      this.providers = [];
      this.servicesLoading = true;
      this.serviceId = "";
      this.serviceOfferingKey = "";
      this.providerId = "any";
      this.groupTitle = "";
      this.groupProviderId = "";
      this.sharedChoiceId = "";
      this.sharedChoiceProviderId = "";
      this.live = false;
      this.liveAvail = {};
      this.liveRawSlots = [];
      this.dayCounts = {};
      this.loadedDays = new Set();
      this.availability.send({
        type: "QUERY.CHANGED",
        query: disabledAvailabilityQuery(),
      });
      this.providersByService = {};
      this.selectedSlot = null;
      this.bookingAction = CommerceRequestedAction.Book;
      this.signedInName = "";
      void this.loadData(authorityKey);
      void this.loadMember(authorityKey);
    }
  }

  private discoveryAuthorityKey(): string {
    return JSON.stringify({
      orgId: this.config?.orgId ?? 0,
      options: this.config?.options ?? {},
      viewerRevision: this.runtime.viewerRevision ?? "guest",
    });
  }

  private async loadMember(authorityKey: string): Promise<void> {
    const dashboardName = this.runtime.customer?.displayName?.trim();
    if (dashboardName) {
      this.signedInName = dashboardName.split(/\s+/)[0] || dashboardName;
      return;
    }
    const orgId = this.config?.orgId;
    if (!orgId) return;
    const ctx = await fetchMemberContext(orgId);
    if (authorityKey !== this.loadedAuthorityKey) return;
    if (ctx?.firstName) this.signedInName = ctx.firstName;
  }

  private logout(): void {
    if (this.config?.orgId) clearEmbedToken(this.config.orgId);
    this.signedInName = "";
  }

  private get viewerTz(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return "";
    }
  }

  private get displayTz(): string {
    return this.selectedTz || this.orgTimezone || this.viewerTz;
  }

  private renderSignin(): TemplateResult {
    if (this.signedInName) {
      // A4: reuse the exact "signin zui-btn-secondary" classes + icon the
      // signed-out state uses below, rather than hand-copying font-size/weight/
      // letter-spacing values, so Sign out can never drift out of sync with
      // Sign in again.
      return html`<button
        class="signin zui-btn-secondary"
        type="button"
        @click=${() => this.logout()}
      >
        ${ICON_SIGNIN} Sign out
      </button>`;
    }
    return html`<button
      class="signin zui-btn-secondary"
      type="button"
      @click=${() =>
        openSignIn(
          this.config?.orgId ?? 0,
          this.accent,
          (name) => (this.signedInName = name || "there"),
        )}
    >
      ${ICON_SIGNIN} Sign in
    </button>`;
  }

  private renderHead(): TemplateResult {
    return html`<div class="head">
      <span></span>
      ${this.opts.hideSignIn ? nothing : this.renderSignin()}
    </div>`;
  }

  private renderTzPicker(): TemplateResult {
    const tz = this.displayTz;
    // Every zone the engine knows, U.S. first, with the studio's and the
    // visitor's own pinned at the top — a visitor abroad could previously only
    // read these times in one of four U.S. zones.
    const groups = timezoneGroups([
      { tz: this.orgTimezone, label: "Studio time" },
      { tz: this.viewerTz, label: "Your time zone" },
    ]);
    return html`<div class="tz-pill">
      ${ICON_CLOCK}
      <select
        .value=${tz}
        @change=${(e: Event) =>
          (this.selectedTz = (e.target as HTMLSelectElement).value)}
      >
        ${groups.map(
          (g) =>
            html`<optgroup label=${g.label}>
              ${g.zones.map(
                (o) => html`<option value=${o.tz}>${o.label}</option>`,
              )}
            </optgroup>`,
        )}
      </select>
    </div>`;
  }

  private async loadData(authorityKey: string): Promise<void> {
    if (!this.config) {
      if (authorityKey === this.loadedAuthorityKey) {
        this.servicesLoading = false;
      }
      return;
    }
    if (!this.config.orgId) {
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.services = MOCK_SERVICES;
      this.providers = MOCK_PROVIDERS;
      this.servicesLoading = false;
      return;
    }
    try {
      const data = await fetchAppointmentData(this.config.orgId);
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.orgTimezone = data.orgTimezone;
      // PLAT-5624: ordering only. An embed-only org (no published site) gets
      // `[]` back here and still groups correctly — the buckets come from the
      // services' own tags.
      this.orgGroups = data.orgGroups ?? [];
      if (data.services.length) {
        this.services = data.services;
        this.providers = data.providers;
        this.providersByService = data.providersByService;
        this.live = true;
        return;
      }
    } catch {
      /* live org fetch failed — leave empty */
    } finally {
      if (authorityKey === this.loadedAuthorityKey) {
        this.servicesLoading = false;
      }
    }
  }

  /** Org-local `YYYY-MM-DD` for the calendar day the guest selected. */
  private dayKeyForDate(d: Date): string {
    return orgWallDayKey(d, this.orgTimezone);
  }

  /** Concrete provider user id for availability/booking, resolving "First Available". */
  private resolvedProviderUserId(): number | null {
    if (this.providerId && this.providerId !== "any") {
      const n = Number(this.providerId);
      return Number.isFinite(n) ? n : null;
    }
    const providers = this.providersByService[this.serviceId] ?? [];
    return providers.length === 1 ? providers[0] : null;
  }

  /** Slots for a day — live cache when available, else deterministic mock. */
  private slotsFor(d: Date): DaySlots {
    if (this.live && this.liveRawSlots.length) {
      const dayKey = this.dayKeyForDate(d);
      const tz = this.displayTz;
      const out: DaySlots = { morning: [], afternoon: [], evening: [] };
      for (const s of this.liveRawSlots) {
        if (orgAppointmentDayKey(s.startTime, tz) !== dayKey) continue;
        const label = formatOrgLocalTime(s.startTime, tz);
        const hour = orgLocalHour(s.startTime, tz);
        const part: keyof DaySlots =
          hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
        out[part].push(label);
      }
      return out;
    }
    if (this.live) return this.liveAvail[this.dayKeyForDate(d)] ?? EMPTY_DAY;
    return slotsForProviderDay(this.providerId, d);
  }

  /** PLAT-6790: whether the strip should offer `d` (counts, not slots). */
  private dayHasOpenings(d: Date): boolean {
    if (!this.live) return totalSlots(this.slotsFor(d)) > 0;
    return (this.dayCounts[this.dayKeyForDate(d)] ?? 0) > 0;
  }

  /** PLAT-6790: the selected day has openings whose times haven't loaded. */
  private get selectedDayPending(): boolean {
    if (!this.live) return false;
    const key = this.dayKeyForDate(this.date);
    return (this.dayCounts[key] ?? 0) > 0 && !this.loadedDays.has(key);
  }

  /** PLAT-6790: pick a day in the strip and load its times if needed. */
  private pickDate(d: Date): void {
    this.date = d;
    this.pumpAvailability();
  }

  /** Fetch real availability for the selected service+provider, a week at a time. */
  private loadAvailability(): void {
    this.liveAvail = {};
    this.liveRawSlots = [];
    this.dayCounts = {};
    this.loadedDays = new Set();
    this.availWindowsDone = new Set();
    this.availSearching = false;
    this.availBase = null;
    this.availOrigin = null;
    const puid =
      this.live && this.config && this.serviceId
        ? this.resolvedProviderUserId()
        : null;
    if (!this.config || puid == null) {
      this.availability.send({
        type: "QUERY.CHANGED",
        query: disabledAvailabilityQuery(),
      });
      return;
    }
    this.availBase = {
      enabled: true,
      orgId: this.config.orgId,
      providerUserId: puid,
      serviceId: this.serviceId,
      timezone: this.orgTimezone,
    };
    this.availOrigin = new Date();
    this.availSearching = true;
    // A new selection supersedes whatever is loading (the machine aborts it).
    this.pumpAvailability(true);
  }

  /** Window index containing `d` (whole days since the origin, in weeks). */
  private availWindowOf(d: Date): number {
    const origin = this.availOrigin!;
    const days = Math.floor(
      (startOfLocalDay(d).getTime() - startOfLocalDay(origin).getTime()) /
        86400000,
    );
    return Math.floor(days / APPT_WINDOW_DAYS);
  }

  /**
   * The next window this view needs: during the first-available search, the
   * next week forward; otherwise any unloaded week the visible strip covers.
   */
  private nextAvailWindow(): number | null {
    if (!this.availOrigin) return null;
    const maxIndex = Math.floor(APPT_HORIZON_DAYS / APPT_WINDOW_DAYS);
    if (this.availSearching) {
      const searchMax = Math.ceil(APPT_FIRST_AVAILABLE_DAYS / APPT_WINDOW_DAYS);
      for (let i = 0; i < searchMax; i++) {
        if (!this.availWindowsDone.has(i)) return i;
      }
      this.availSearching = false;
    }
    const first = Math.max(0, this.availWindowOf(this.weekStart));
    const last = this.availWindowOf(addDays(this.weekStart, 6));
    for (let i = first; i <= Math.min(last, maxIndex); i++) {
      if (!this.availWindowsDone.has(i)) return i;
    }
    return null;
  }

  /** Request the next needed window, unless one is already loading. */
  private pumpAvailability(supersede = false): void {
    if (!this.availBase || !this.availOrigin) return;
    if (this.availLoading && !supersede) return;
    // The selected day's times come first once we know it has openings.
    const dayKey = this.dayKeyForDate(this.date);
    if (
      !this.availSearching &&
      (this.dayCounts[dayKey] ?? 0) > 0 &&
      !this.loadedDays.has(dayKey)
    ) {
      // A generous range around the day: the server keeps only `day`'s slots,
      // judged in the org zone, so no browser-side zone math is needed.
      const from = addDays(startOfLocalDay(this.date), -1);
      this.availability.send({
        type: "QUERY.CHANGED",
        query: {
          ...this.availBase,
          startsAt: from.toISOString(),
          endsAt: addDays(from, 3).toISOString(),
          day: dayKey,
        },
      });
      return;
    }
    const index = this.nextAvailWindow();
    if (index == null) return;
    const from = addDays(startOfLocalDay(this.availOrigin), index * APPT_WINDOW_DAYS);
    // Window 0 starts now, not midnight: past times are never bookable.
    const startsAt = index === 0 ? this.availOrigin : from;
    this.availability.send({
      type: "QUERY.CHANGED",
      query: {
        ...this.availBase,
        startsAt: startsAt.toISOString(),
        endsAt: addDays(from, APPT_WINDOW_DAYS).toISOString(),
        day: "",
      },
    });
  }

  private applyAvailability(
    query: AppointmentAvailabilityQuery,
    result: AppointmentAvailabilityResult | null,
  ): void {
    const base = this.availBase;
    if (
      !query.enabled ||
      !base ||
      !this.availOrigin ||
      query.providerUserId !== base.providerUserId ||
      query.serviceId !== base.serviceId
    ) {
      return; // a previous selection's late reply
    }
    if (query.day) {
      // One day's times. A failure is marked loaded too, so it isn't retried
      // in a loop; the visitor sees "No openings" and can pick again.
      this.loadedDays = new Set(this.loadedDays).add(query.day);
      if (result) {
        const seen = new Set(this.liveRawSlots.map((slot) => slot.startTime));
        this.liveRawSlots = [
          ...this.liveRawSlots,
          ...result.slots.filter((slot) => !seen.has(slot.startTime)),
        ];
      }
    } else {
      const index = this.availWindowOf(new Date(query.endsAt)) - 1;
      this.availWindowsDone = new Set(this.availWindowsDone).add(index);
      if (result) this.dayCounts = { ...this.dayCounts, ...result.counts };
      if (this.availSearching) {
        const first = Object.keys(this.dayCounts)
          .filter((key) => this.dayCounts[key] > 0)
          .sort()[0];
        if (first) {
          const [y, m, day] = first.split("-").map(Number);
          this.date = new Date(y, m - 1, day);
          this.weekStart = this.date;
          this.availSearching = false;
        } else if (!result) {
          this.availSearching = false; // failed: stop walking, allow retry by navigation
        }
      }
    }
    // Let the machine settle out of `loading` before asking for more.
    queueMicrotask(() => this.pumpAvailability());
  }

  /** Move the visible week and load it if needed. */
  private shiftAvailWeek(delta: number): void {
    this.weekStart = addDays(this.weekStart, delta * 7);
    this.pumpAvailability();
  }

  protected renderContent(_config: EmbedConfig): TemplateResult {
    // AP-CO: step 3 (checkout) hands off entirely to the shared
    // <zipper-booking-checkout> — its own two-column layout + sticky Order
    // Summary + back button + confirmation replace the widget's `.wrap`/crumbs
    // frame, exactly as the class flow does when a class is booked.
    if (this.step === 3) return this.renderCheckout();
    return html`<div class="wrap">
      ${this.renderHead()} ${this.renderCrumbs()}
      ${this.step === 1 ? this.renderStep1() : this.renderPick()}
    </div>`;
  }

  /** Step 1 (service selection). Holds a loading skeleton on the live path
   * until the real catalog resolves, so no sample services flash first (AP-LOAD). */
  private renderStep1(): TemplateResult {
    if (this.servicesLoading) {
      return html`${this.renderTitle()} ${this.renderSkeleton()}`;
    }
    // PLAT-5624: the group drill-down lives INSIDE step 1 (group → provider →
    // that provider's type), so `renderCrumbs()` still reads Service › Time ›
    // Confirm. If the group disappeared (pre-filters/data changed under us),
    // fall through to the top-level list rather than rendering nothing.
    if (this.sharedChoiceId) {
      const sharedChoice = this.currentSharedChoice();
      if (sharedChoice) return this.renderSharedChoice(sharedChoice);
    }
    if (this.groupTitle) {
      const group = this.groupModel.groups.find(
        (g) => g.id === this.groupTitle,
      );
      if (group) {
        return this.groupProviderId
          ? this.renderGroupServices(group)
          : this.renderGroupProviders(group);
      }
    }
    return this.renderServices();
  }

  /**
   * PLAT-5624: the merged step-1 model. Deliberately built from
   * `visibleServices()` — i.e. AFTER the builder pre-filters — so grouping can
   * never resurrect a service the embed's settings filtered out.
   */
  private get groupModel(): AppointmentGroupsResult {
    return buildAppointmentGroups(
      this.visibleServices(),
      this.providers,
      this.orgGroups,
      this.providersByService,
      this.allowedGroupProviderIds,
    );
  }

  /**
   * Provider pre-filter used by the group builder. `visibleServices()` can
   * decide that a service survives when ANY mapped provider is allowed, but
   * the builder must also filter that service's provider mapping or it would
   * resurrect the excluded providers as drill-down rows.
   */
  private get allowedGroupProviderIds(): ReadonlySet<string> | undefined {
    const ids = this.opts.filterInstructors;
    return ids?.length ? new Set(ids) : undefined;
  }

  /** The `{group, provider}` pair currently drilled into, if any. */
  private groupProviderEntry(): AppointmentGroupProvider | undefined {
    if (!this.groupTitle || !this.groupProviderId) return undefined;
    return this.groupModel.groups
      .find((g) => g.id === this.groupTitle)
      ?.providers.find((p) => p.provider.id === this.groupProviderId);
  }

  private sharedChoiceScope(): AppointmentService[] {
    if (this.groupTitle) {
      const group = this.groupModel.groups.find(
        (candidate) => candidate.id === this.groupTitle,
      );
      if (!group) return [];
      if (this.groupProviderId) {
        return (
          group.providers.find(
            (entry) => entry.provider.id === this.groupProviderId,
          )?.services ?? []
        );
      }
      return group.services;
    }
    return this.groupModel.ungrouped;
  }

  private currentSharedChoice(): SharedAppointmentDisplayOption | undefined {
    return groupSharedAppointmentServices(this.sharedChoiceScope()).find(
      (option) => option.id === this.sharedChoiceId,
    );
  }

  private clearSharedChoice(): void {
    this.sharedChoiceId = "";
    this.sharedChoiceProviderId = "";
  }

  private pickSharedChoice(
    option: SharedAppointmentDisplayOption,
    providerId?: string,
  ): void {
    const scopedServices = providerId
      ? option.services.filter(
          (service) => String(service.providerUserId) === providerId,
        )
      : option.services;
    if (scopedServices.length === 1) {
      const service = scopedServices[0];
      if (service.providerUserId != null) {
        this.providerId = String(service.providerUserId);
      }
      this.pickService(service);
      return;
    }

    const providerIds = sharedAppointmentProviderIds(option);
    this.sharedChoiceId = option.id;
    this.sharedChoiceProviderId =
      providerId ?? (providerIds.length === 1 ? String(providerIds[0]) : "");
    this.serviceId = "";
    this.serviceOfferingKey = "";
    this.providerId = "any";
    this.selectedSlot = null;
    this.liveAvail = {};
    this.liveRawSlots = [];
  }

  private pickSharedChoiceProvider(
    option: SharedAppointmentDisplayOption,
    providerId: string,
  ): void {
    const services = option.services.filter(
      (service) => String(service.providerUserId) === providerId,
    );
    if (services.length === 1) {
      this.providerId = providerId;
      this.pickService(services[0]);
      return;
    }
    this.sharedChoiceProviderId = providerId;
  }

  /** Connie 7/20: honor hideTitle so "Book an appointment" can be suppressed. */
  private renderTitle(): TemplateResult | typeof nothing {
    return this.opts.hideTitle
      ? nothing
      : html`<h2 class="title">Book an appointment</h2>`;
  }

  private renderCrumbs(): TemplateResult {
    const c = (n: number, label: string) =>
      html`<span class=${this.step === n ? "on" : ""}>${label}</span>`;
    return html`<div class="crumbs">
      ${c(1, "Service")} › ${c(2, "Time")} › ${c(3, "Confirm")}
    </div>`;
  }

  /**
   * ST1: embed-level pre-filter (set in settings) narrows the service list
   * before it's grouped/rendered. Empty/absent array = pass-through, mirroring
   * the schedule's `filtered()` (Set membership; empty = show all).
   *  - filterServiceIds → the service/appointment-type id (matches `s.id`).
   *  - filterInstructors → the concrete row's `providerUserId` when present;
   *    `providersByService` is only the legacy fallback. A shared bare id can
   *    map to multiple providers, but that must not keep an excluded
   *    provider-owned price/product row visible.
   * A location pre-filter has no service-level dimension to match against in the
   * current data model (services aren't location-scoped), so it isn't applied.
   */
  private visibleServices(): AppointmentService[] {
    const o = this.opts;
    const preSvc = o.filterServiceIds?.length
      ? new Set(o.filterServiceIds)
      : null;
    const preInst = o.filterInstructors?.length
      ? new Set(o.filterInstructors)
      : null;
    if (!preSvc && !preInst) return this.services;
    return this.services.filter((s) => {
      if (preSvc && !preSvc.has(s.id)) return false;
      if (preInst) {
        const providerIds =
          s.providerUserId != null
            ? [s.providerUserId]
            : (this.providersByService[s.id] ?? []);
        if (!providerIds.some((uid) => preInst.has(String(uid)))) return false;
      }
      return true;
    });
  }

  /**
   * PLAT-5624: step 1's top-level list — merged GROUP cards first, then every
   * type that no surviving group represents, through the existing (unmodified)
   * category/service rendering.
   *
   * An org with zero grouped types takes the early return and renders exactly
   * what it rendered before this change.
   */
  private renderServices(): TemplateResult {
    const o = this.opts;
    const services = this.visibleServices();
    const { groups, ungrouped } = buildAppointmentGroups(
      services,
      this.providers,
      this.orgGroups,
      this.providersByService,
      this.allowedGroupProviderIds,
    );
    if (!groups.length) {
      return html`${this.renderTitle()} ${this.renderCategorized(services, o)}`;
    }
    return html`
      ${this.renderTitle()} ${groups.map((g) => this.renderGroupRow(g, o))}
      ${this.renderCategorized(ungrouped, o)}
    `;
  }

  /** The pre-existing category-sectioned service list, unchanged, extracted so
   * the group screen can reuse it for the ungrouped remainder. */
  private renderCategorized(
    services: AppointmentService[],
    o: AppointmentOptions,
  ): TemplateResult {
    const cats = [...new Set(services.map((s) => s.category))];
    // Only surface collapsible section headers when there's more than one
    // category to group by; a single-category list stays flat (unchanged).
    const grouped = cats.length > 1;
    return html`
      ${cats.map((cat) => {
        const items = services.filter((s) => s.category === cat);
        const options = groupSharedAppointmentServices(items);
        const collapsed = this.collapsedCats.has(cat);
        return html`
          ${grouped
            ? html`<button
                class="cat-head"
                type="button"
                aria-expanded=${String(!collapsed)}
                @click=${() => this.toggleCategory(cat)}
              >
                <span class="cat-label"
                  >${cat} · ${options.length}
                  option${options.length === 1 ? "" : "s"}</span
                >
                <span class="cat-caret ${collapsed ? "" : "open"}"
                  >${ICON_CARET}</span
                >
              </button>`
            : nothing}
          ${grouped && collapsed
            ? nothing
            : options.map((option) =>
                option.sharedAppointmentTypeId && option.services.length > 1
                  ? this.renderSharedChoiceRow(option, o)
                  : this.renderServiceRow(option.services[0], o),
              )}
        `;
      })}
    `;
  }

  /**
   * PLAT-5624: a merged GROUP card on step 1 — one "Wellness" row standing in
   * for every provider's copy of the types tagged into it (which is what made
   * "60-Minute Massage" render once per provider).
   *
   * Reuses the `.svc` / `.svc-main` / `.nm` / `.price` classes so existing spec
   * and POM reads keep working; `data-kind="group"` is the only new hook, and
   * it is what lets `serviceNames()` keep meaning "bookable services".
   *
   * A group has no image of its own: borrow the first member's, else the shared
   * photo GLYPH — never a letter from the title (AP2).
   */
  private renderGroupRow(
    g: AppointmentGroup,
    o: AppointmentOptions,
  ): TemplateResult {
    const img = mergedImage(g.services);
    // Namespaced so a group can never collide with a service id in the
    // loaded/errored thumbnail latches.
    const key = `group:${g.id}`;
    const n = g.providers.length;
    const dur = mergedDurationLabel(g.services);
    // Empty when the members are all credit-priced/free: drop the slot rather
    // than print "Free" over a service that still needs a credit.
    const price = mergedPriceLabel(g.services, this.config?.currency);
    return html`<div class="svc" data-kind="group">
      <button class="svc-main" @click=${() => this.pickGroup(g.id)}>
        ${this.renderRowThumb(key, img, o)}
        <span style="min-width:0;flex:1">
          <span class="nm-row"><span class="nm">${g.title}</span></span>
          <span class="meta"
            >${n} provider${n === 1 ? "" : "s"}${dur ? ` · ${dur}` : ""}</span
          >
        </span>
      </button>
      ${o.hidePrices || !price
        ? nothing
        : html`<span class="price">${price}</span>`}
      <span class="go" aria-hidden="true">${ICON_CHEVRON_R}</span>
    </div>`;
  }

  private renderSharedChoiceRow(
    option: SharedAppointmentDisplayOption,
    o: AppointmentOptions,
  ): TemplateResult {
    const providerCount = sharedAppointmentProviderIds(option).length;
    const image = mergedImage(option.services);
    const price = mergedPriceLabel(option.services);
    return html`<div class="svc" data-kind="shared-appointment">
      <button class="svc-main" @click=${() => this.pickSharedChoice(option)}>
        ${this.renderRowThumb(`shared:${option.id}`, image, o)}
        <span style="min-width:0;flex:1">
          <span class="nm-row"><span class="nm">${option.name}</span></span>
          <span class="meta"
            >${option.duration} min · ${providerCount}
            provider${providerCount === 1 ? "" : "s"}</span
          >
        </span>
      </button>
      ${o.hidePrices || !price
        ? nothing
        : html`<span class="price">${price}</span>`}
      <span class="go" aria-hidden="true">${ICON_CHEVRON_R}</span>
    </div>`;
  }

  private renderSharedChoice(
    option: SharedAppointmentDisplayOption,
  ): TemplateResult {
    const providerIds = sharedAppointmentProviderIds(option).map(String);
    if (!this.sharedChoiceProviderId) {
      const providers = this.providers.filter(
        (provider) => !provider.generic && providerIds.includes(provider.id),
      );
      return html`
        <button class="back" @click=${() => this.clearSharedChoice()}>
          ${ICON_BACK} Service
        </button>
        <h2 class="title">Choose a provider</h2>
        ${providers.map(
          (provider) =>
            html`<div class="svc" data-kind="provider">
              <button
                class="svc-main"
                @click=${() =>
                  this.pickSharedChoiceProvider(option, provider.id)}
              >
                ${this.renderRowThumb(
                  `shared-provider:${option.id}:${provider.id}`,
                  provider.photo,
                  this.opts,
                  "avatar",
                )}
                <span style="min-width:0;flex:1">
                  <span class="nm-row"
                    ><span class="nm">${provider.name}</span></span
                  >
                  <span class="meta">${option.duration} min</span>
                </span>
              </button>
              <span class="go" aria-hidden="true">${ICON_CHEVRON_R}</span>
            </div>`,
        )}
      `;
    }

    const services = option.services.filter(
      (service) =>
        String(service.providerUserId) === this.sharedChoiceProviderId,
    );
    const provider = this.providers.find(
      (candidate) => candidate.id === this.sharedChoiceProviderId,
    );
    return html`
      <button
        class="back"
        @click=${() => {
          if (providerIds.length > 1) this.sharedChoiceProviderId = "";
          else this.clearSharedChoice();
        }}
      >
        ${ICON_BACK} ${providerIds.length > 1 ? "Providers" : "Service"}
      </button>
      <h2 class="title">Choose a schedule</h2>
      <div class="gcrumb">
        <span>${option.name} · ${option.duration} min</span>
        ${provider
          ? html`<span class="gcrumb-sep">›</span><span>${provider.name}</span>`
          : nothing}
      </div>
      ${services.map((service) =>
        this.renderServiceRow(
          service,
          this.opts,
          service.scheduleLabel || "Schedule",
        ),
      )}
    `;
  }

  /**
   * PLAT-5624: pick a provider WITHIN a group (screen 2 of the drill-down),
   * mirroring `ZipperSchedulingBlockRevamp.renderProviderSelection()`.
   *
   * This screen exists because the provider is what resolves the bookable
   * appointment type: each provider owns their own copy with its own duration,
   * price and `stripeProductId`, so "Wellness" alone is not yet bookable.
   */
  private renderGroupProviders(g: AppointmentGroup): TemplateResult {
    const o = this.opts;
    return html`
      <button class="back" @click=${() => this.backToGroups()}>
        ${ICON_BACK} Service
      </button>
      ${this.renderGroupCrumb(g.title)}
      <h2 class="title">Choose a provider</h2>
      ${g.providers.map((p) => {
        const n = p.services.length;
        const dur = mergedDurationLabel(p.services);
        const price = mergedPriceLabel(p.services, this.config?.currency);
        return html`<div class="svc" data-kind="provider">
          <button
            class="svc-main"
            @click=${() => this.pickGroupProvider(p.provider.id)}
          >
            ${this.renderRowThumb(
              `provider:${p.provider.id}`,
              p.provider.photo,
              o,
              "avatar",
            )}
            <span style="min-width:0;flex:1">
              <span class="nm-row"
                ><span class="nm">${p.provider.name}</span></span
              >
              <span class="meta"
                >${n} option${n === 1 ? "" : "s"}${dur ? ` · ${dur}` : ""}</span
              >
            </span>
          </button>
          ${o.hidePrices || !price
            ? nothing
            : html`<span class="price">${price}</span>`}
          <span class="go" aria-hidden="true">${ICON_CHEVRON_R}</span>
        </div>`;
      })}
    `;
  }

  /**
   * PLAT-5624: screen 3 of the drill-down — the chosen provider's own
   * appointment types inside the group. Only reached when they offer more than
   * one (a single type goes straight to the time picker), and rendered with the
   * UNMODIFIED `renderServiceRow()` so these keep the description accordion,
   * thumbnail and price treatment of any other service row.
   */
  private renderGroupServices(g: AppointmentGroup): TemplateResult {
    const o = this.opts;
    const entry = g.providers.find(
      (p) => p.provider.id === this.groupProviderId,
    );
    if (!entry) return this.renderGroupProviders(g);
    const options = groupSharedAppointmentServices(entry.services);
    return html`
      <button class="back" @click=${() => (this.groupProviderId = "")}>
        ${ICON_BACK} ${g.title}
      </button>
      ${this.renderGroupCrumb(g.title, entry.provider.name)}
      <h2 class="title">Choose a service</h2>
      ${options.map((option) =>
        option.sharedAppointmentTypeId && option.services.length > 1
          ? this.renderSharedChoiceRow(option, o)
          : this.renderServiceRow(option.services[0], o),
      )}
    `;
  }

  /** `Wellness ›` / `Wellness › Lena Ortiz ›` trail, each hop clickable. */
  private renderGroupCrumb(
    title: string,
    providerName?: string,
  ): TemplateResult {
    return html`<div class="gcrumb">
      <button
        class="gcrumb-link"
        type="button"
        @click=${() => this.backToGroups()}
      >
        ${title}
      </button>
      <span class="gcrumb-sep">›</span>
      ${providerName
        ? html`<button
              class="gcrumb-link"
              type="button"
              @click=${() => (this.groupProviderId = "")}
            >
              ${providerName}
            </button>
            <span class="gcrumb-sep">›</span>`
        : nothing}
    </div>`;
  }

  /** Shared thumbnail slot for the merged group/provider rows: the image when
   * it has one and hasn't 404'd, otherwise the photo glyph (never a letter). */
  private renderRowThumb(
    key: string,
    url: string | undefined,
    o: AppointmentOptions,
    extraClass = "",
  ): TemplateResult | typeof nothing {
    if (o.hideThumbnail) return nothing;
    const usable =
      !!url && (this.loadedThumbIds.has(key) || !this.erroredThumbIds.has(key));
    if (!usable) {
      return html`<span class="thumb thumb-ph" aria-hidden="true"
        >${ICON_IMAGE}</span
      >`;
    }
    return html`<img
      class="thumb ${extraClass}"
      src=${url as string}
      alt=""
      @load=${() => this.markThumbLoaded(key)}
      @error=${(e: Event) =>
        this.markThumbBroken(key, e.target as HTMLImageElement)}
    />`;
  }

  /**
   * PLAT-5624: drill into a group. Any in-flight service/provider/slot
   * selection is dropped: inside a group the PROVIDER is what determines which
   * appointment type (and therefore which price and Stripe product) gets
   * booked, so carrying a stale one forward could mis-charge.
   */
  private pickGroup(groupId: string): void {
    this.clearSharedChoice();
    this.groupTitle =
      this.groupModel.groups.find(
        (group) => group.id === groupId || group.title === groupId,
      )?.id ?? groupId;
    this.groupProviderId = "";
    this.serviceId = "";
    this.serviceOfferingKey = "";
    this.providerId = "any";
    this.selectedSlot = null;
    this.liveAvail = {};
    this.liveRawSlots = [];
    this.dayCounts = {};
    this.loadedDays = new Set();
  }

  /**
   * PLAT-5624: pick a provider inside a group. Exactly one type under this
   * group → straight to the time picker; more than one → the compact type list
   * first. `providerId` is pinned BEFORE `pickService()` so the availability
   * fetch it kicks off resolves against this provider, not "First Available".
   */
  private pickGroupProvider(providerId: string): void {
    this.groupProviderId = providerId;
    this.providerId = providerId;
    const options = groupSharedAppointmentServices(
      this.groupProviderEntry()?.services ?? [],
    );
    if (options.length === 1) {
      this.pickSharedChoice(options[0], providerId);
    }
  }

  /** Back out of the group entirely, to the top-level step-1 list. */
  private backToGroups(): void {
    this.clearSharedChoice();
    this.groupTitle = "";
    this.groupProviderId = "";
    this.serviceId = "";
    this.serviceOfferingKey = "";
    this.providerId = "any";
  }

  /** One service card: an inner button selects the service; a sibling caret
   * toggles the description accordion (APT-35), mirroring the schedule row's
   * expand pattern. The caret only renders when a description exists so live
   * rows without one show no dead affordance. */
  private renderServiceRow(
    s: AppointmentService,
    o: AppointmentOptions,
    displayName?: string,
  ): TemplateResult {
    const key = appointmentServiceOfferingKey(s);
    const expanded = this.expandedServiceIds.has(key);
    return renderAppointmentService(
      displayName ? { ...s, name: displayName } : s,
      {
        options: o,
        expanded,
        thumbnailLoaded: this.loadedThumbIds.has(key),
        thumbnailErrored: this.erroredThumbIds.has(key),
        formattedPrice: this.formatServicePrice(s),
        onSelect: () => this.pickService(s),
        onToggleExpanded: () => this.toggleServiceExpanded(key),
        onThumbnailLoaded: () => this.markThumbLoaded(key),
        onThumbnailError: (image) => this.markThumbBroken(key, image),
      },
    );
  }

  /** AP-IMG: a service thumbnail has painted (`@load`). Latch it as loaded so
   * it can NEVER fall back to the glyph afterwards, and clear any stale error
   * flag. This is the durable fix for the flash-to-placeholder: the card kept
   * its <img> mounted (stable element identity), but a transient `error` fired
   * on an unrelated re-render used to re-latch the fallback — once loaded, that
   * is now ignored (see markThumbBroken). */
  private markThumbLoaded(id: string): void {
    if (this.loadedThumbIds.has(id)) {
      // Already loaded, but a prior spurious error may have latched — clear it.
      if (!this.erroredThumbIds.has(id)) return;
    }
    if (!this.loadedThumbIds.has(id)) {
      const loaded = new Set(this.loadedThumbIds);
      loaded.add(id);
      this.loadedThumbIds = loaded;
    }
    if (this.erroredThumbIds.has(id)) {
      const errored = new Set(this.erroredThumbIds);
      errored.delete(id);
      this.erroredThumbIds = errored;
    }
  }

  /** AP2: a service thumbnail 404'd/failed to load — record it so the card
   * re-renders with the photo-glyph placeholder instead of a broken image.
   * Guarded so the placeholder swap (which drops the img) can't re-fire.
   *
   * AP-IMG: only latch on a GENUINE, never-loaded failure. A valid thumbnail
   * used to flash to the placeholder because the `error` handler latched on
   * *any* error event — including spurious ones the browser can fire while the
   * <img> is re-evaluated during a Lit re-render, even though the image had
   * already decoded. So: (1) if the image ever painted (`loadedThumbIds`), we
   * never fall back; (2) otherwise a successfully-loaded image reports
   * `naturalWidth > 0`, whereas a real 404/broken URL settles as `complete`
   * with `naturalWidth === 0`, so we gate on that and ignore spurious events. */
  private markThumbBroken(id: string, img?: HTMLImageElement): void {
    if (this.loadedThumbIds.has(id)) return; // painted once → never fall back
    if (img && !(img.complete && img.naturalWidth === 0)) return;
    if (this.erroredThumbIds.has(id)) return;
    const next = new Set(this.erroredThumbIds);
    next.add(id);
    this.erroredThumbIds = next;
  }

  private formatServicePrice(s: AppointmentService): string {
    if (s.price > 0) return formatShortAmount(s.price, this.config?.currency);
    // PLAT-5624: `price: 0` alone can't tell a genuinely free service from a
    // credit-priced one — only the org's own `isFree` switch can. Labelling a
    // free service "Use credits" (and vice versa) was the visible half of the
    // same ambiguity that let a credit-priced appointment book for $0.
    //
    // An appointment carrying no drop-in fee usually isn't free either: it
    // takes credits, and its `isFree` flag rides along with "no drop-in price"
    // often enough that printing "Free" promises something the booking flow
    // won't honour. Say nothing rather than something wrong — the card still
    // reads cleanly as name + duration, and checkout states the real terms.
    return s.isFree ? "" : "Use credits";
  }

  /** APT-35: toggle a service's inline description accordion. */
  private toggleServiceExpanded(id: string): void {
    const next = new Set(this.expandedServiceIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.expandedServiceIds = next;
  }

  /** APT-34: collapse/expand a category section. */
  private toggleCategory(cat: string): void {
    const next = new Set(this.collapsedCats);
    if (next.has(cat)) next.delete(cat);
    else next.add(cat);
    this.collapsedCats = next;
  }

  private pickService(service: string | AppointmentService): void {
    const selected =
      typeof service === "string"
        ? this.services.find((s) => s.id === service)
        : service;
    const id = typeof service === "string" ? service : service.id;
    this.serviceId = id;
    this.serviceOfferingKey = selected
      ? appointmentServiceOfferingKey(selected)
      : "";
    // A reused appointment-type id can represent distinct provider-owned rows.
    // Pin an explicitly clicked row when the bare id maps to several providers;
    // the provider strip can still change it afterward, and `service` resolves
    // the matching provider-owned copy.
    if (
      selected?.providerUserId != null &&
      (this.providersByService[id]?.length ?? 0) > 1
    ) {
      this.providerId = String(selected.providerUserId);
    }
    this.step = 2;
    // Intake forms are now loaded/submitted by the shared checkout
    // (<zipper-booking-checkout>) from the `classForms` prop, so the widget no
    // longer fetches the form itself here.
    if (this.live) {
      void this.loadAvailability();
      return;
    }
    const first = firstAvailableDate(this.providerId, new Date());
    if (first) {
      this.date = first;
      this.weekStart = first;
    }
  }

  private get visibleProviders(): AppointmentProvider[] {
    const ids = new Set(this.providersByService[this.serviceId] ?? []);
    return this.providers.filter(
      (provider) =>
        (!provider.generic || ids.size === 1) &&
        (provider.generic || ids.has(Number(provider.id))),
    );
  }

  private renderPick(): TemplateResult {
    // PLAT-5624: when step 2 was entered through a group the provider is
    // already resolved and is NOT swappable here (see below), and the back
    // button has to return into the group, not to the top-level list.
    const inGroup = this.groupProviderEntry();
    const groupTitle = this.groupModel.groups.find(
      ({ id }) => id === this.groupTitle,
    )?.title;
    const sharedChoice = this.currentSharedChoice();
    const backLabel = sharedChoice
      ? sharedChoice.name
      : !inGroup
        ? "Service"
        : inGroup.services.length > 1
          ? inGroup.provider.name
          : groupTitle || 'Service';
    const days = Array.from({ length: 7 }, (_, i) =>
      addDays(this.weekStart, i),
    );
    const slots = this.slotsFor(this.date);
    const has = totalSlots(slots) > 0;
    // "Today" is only meaningful when the strip has scrolled away from — or the
    // selection has moved off — the current day (AP4).
    const today = new Date();
    const showingToday =
      this.date.toDateString() === today.toDateString() &&
      this.weekStart.toDateString() === today.toDateString();
    const group = (label: string, list: string[]) =>
      list.length
        ? html`<div class="slotgroup">${label}</div>
            <div class="slots">
              ${list.map((t) => {
                const buyout = this.buyoutCtaForTime(t);
                return html`<div class="slot-cell">
                  <button class="slot" @click=${() => this.pickTime(t)}>
                    ${t}
                  </button>
                  ${buyout
                    ? html`<button
                        class="slot slot-buyout"
                        @click=${() =>
                          this.pickTime(t, CommerceRequestedAction.Buyout)}
                      >
                        ${buyout}
                      </button>`
                    : nothing}
                </div>`;
              })}
            </div>`
        : nothing;
    return html`
      <button class="back" @click=${() => this.backFromPick()}>
        ${ICON_BACK} ${backLabel}
      </button>
      <h2 class="title">${this.service?.name}</h2>
      ${inGroup || sharedChoice
        ? // PLAT-5624: no provider strip on the grouped path. Every provider
          // owns a DIFFERENT appointment type here, so swapping providers
          // would silently swap the type — invalidating the loaded slots, the
          // price and the stripeProductId already resolved for checkout. It
          // also removes the generic "First Available", which is unresolvable
          // across providers whose types (and Stripe products) differ. Going
          // back to the group's provider list is the supported way to switch.
          nothing
        : html`<div class="providers">
            ${this.visibleProviders.map(
              (p) =>
                html`<button
                  class="prov"
                  aria-pressed=${p.id === this.providerId}
                  @click=${() => this.pickProvider(p.id)}
                >
                  ${p.photo
                    ? html`<img src=${p.photo} alt="" />`
                    : html`<span class="ph"
                        >${p.generic ? "★" : p.name.charAt(0)}</span
                      >`}
                  ${p.name}
                </button>`,
            )}
          </div>`}
      <div class="pick-head">
        <div class="range">
          ${MONTHS[days[0].getMonth()]}${days[0].getMonth() !==
          days[6].getMonth()
            ? ` – ${MONTHS[days[6].getMonth()]}`
            : ""}
          ${days[6].getFullYear()}
        </div>
        <div class="pick-head-actions">
          ${this.renderTzPicker()}
          <button
            class="today-pill"
            type="button"
            ?disabled=${showingToday}
            @click=${() => this.goToday()}
          >
            Today
          </button>
        </div>
      </div>
      <div class="datestrip">
        <button
          class="arrow"
          aria-label="Previous week"
          @click=${() => this.shiftAvailWeek(-1)}
        >
          ${ICON_CHEVRON_L}
        </button>
        <div class="days">
          ${days.map((d) => {
            const dayHas = this.dayHasOpenings(d);
            return html`<button
              class="day ${dayHas ? "" : "empty"}"
              aria-pressed=${d.toDateString() === this.date.toDateString()}
              ?disabled=${!dayHas}
              @click=${() => this.pickDate(d)}
            >
              <span class="dow">${WEEKDAYS[d.getDay()]}</span>
              <span class="num">${d.getDate()}</span>
            </button>`;
          })}
        </div>
        <button
          class="arrow"
          aria-label="Next week"
          @click=${() => this.shiftAvailWeek(1)}
        >
          ${ICON_CHEVRON_R}
        </button>
      </div>
      ${this.availLoading || this.selectedDayPending
        ? html`<div class="none">Loading times…</div>`
        : has
          ? html`${group("Morning", slots.morning)}
            ${group("Afternoon", slots.afternoon)}
            ${group("Evening", slots.evening)}`
          : html`<div class="none">
              No openings this day — try another date.
            </div>`}
    `;
  }

  /**
   * PLAT-5624: step-2 back. On the flat path this is the service list, exactly
   * as before. Entered through a group it returns INSIDE step 1 — to that
   * provider's type list when they offer more than one, otherwise to the
   * group's provider list (which is the screen the guest actually came from
   * when a single type auto-advanced).
   */
  private backFromPick(): void {
    const entry = this.groupProviderEntry();
    if (entry && entry.services.length <= 1) this.groupProviderId = "";
    this.step = 1;
  }

  /** AP4: reset the visible week strip and the selected day back to today. */
  private goToday(): void {
    const today = new Date();
    this.date = today;
    this.weekStart = today;
    this.pumpAvailability();
  }

  private pickProvider(id: string): void {
    this.providerId = id;
    const providerUserId = Number(id);
    const providerOffering = Number.isFinite(providerUserId)
      ? this.services.find(
          (service) =>
            service.id === this.serviceId &&
            service.providerUserId === providerUserId,
        )
      : undefined;
    if (providerOffering) {
      this.serviceOfferingKey = appointmentServiceOfferingKey(providerOffering);
    }
    if (this.live) {
      void this.loadAvailability();
      return;
    }
    const first = firstAvailableDate(id, new Date());
    if (first) {
      this.date = first;
      this.weekStart = first;
    }
  }

  private pickTime(
    t: string,
    action: CommerceRequestedAction = CommerceRequestedAction.Book,
  ): void {
    this.bookingAction = action;
    this.time = t;
    if (this.live) {
      const dk = this.dayKeyForDate(this.date);
      this.selectedSlot =
        this.liveRawSlots.find(
          (s) =>
            orgAppointmentDayKey(s.startTime, this.displayTz) === dk &&
            formatOrgLocalTime(s.startTime, this.displayTz) === t,
        ) ?? null;
    }
    this.step = 3;
  }

  private slotForLabel(t: string): AppointmentTimeslot | undefined {
    const dk = this.dayKeyForDate(this.date);
    return this.liveRawSlots.find(
      (s) =>
        orgAppointmentDayKey(s.startTime, this.displayTz) === dk &&
        formatOrgLocalTime(s.startTime, this.displayTz) === t,
    );
  }

  private slotIsEmpty(
    slot: AppointmentTimeslot,
    appointmentTypeId: string,
  ): boolean {
    const liveSlot = slot as AppointmentTimeslot & {
      buyoutOccupied?: boolean;
      bookingCountsPerAppointmentType?: {
        appointmentTypeId?: string;
        count?: number;
      }[];
    };
    if (liveSlot.buyoutOccupied) return false;
    const counts = liveSlot.bookingCountsPerAppointmentType ?? [];
    const booked =
      counts.find((count) => count.appointmentTypeId === appointmentTypeId)
        ?.count ?? 0;
    return booked === 0;
  }

  private buyoutCtaForTime(t: string): string | undefined {
    const svc = this.service;
    if (
      svc?.allowBuyout !== true ||
      typeof svc.buyoutPrice !== "number" ||
      svc.buyoutPrice <= 0
    ) {
      return undefined;
    }
    if (this.live) {
      const slot = this.slotForLabel(t);
      if (!slot || !this.slotIsEmpty(slot, svc.id)) {
        return undefined;
      }
    }
    return `Buy out — ${formatMinorAmount(
      Math.round(svc.buyoutPrice * 100),
      svc.buyoutCurrency,
    )}`;
  }

  /**
   * AP-CO: the appointment checkout screen. Renders the shared
   * <zipper-booking-checkout> in kind="appointment" mode, populated from the
   * widget's booking state, so appointments get the same two-column layout +
   * sticky Order Summary + payment + confirmation as the class flow. The shared
   * component owns the sign-in banner, "Your details", the intake forms (loaded
   * from `classForms`), payment, and its own <zipper-confirmation
   * kind="appointment"> — which carries the AP-CONF "Book another appointment"
   * callout.
   */
  /**
   * PLAT-5624: creating the real appointment is a PRECONDITION of the
   * checkout's confirmation, not a side effect of it — `<zipper-booking-checkout>`
   * awaits this hook and only then flips to "You're booked!".
   *
   * A stable reference: a fresh arrow per render would mark the child's
   * `finalizeBooking` property changed on every parent update.
   */
  private readonly finalizeBookingBound = (detail: {
    buyer?: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    intakeSubmissions?: { formId: number; formResponseId: string }[];
    stripeProductId?: string;
    consumeCreditForProductId?: string;
    deferredMembershipPurchaseId?: string | undefined;
    deferredBeneficiaryRosterId?: number | undefined;
    checkoutToken?: string | undefined;
    targetUserId?: number;
    currentUserId?: number;
  }): Promise<void> => this.finalizeAppointment(detail);

  private renderCheckout(): TemplateResult {
    const svc = this.service;
    if (!svc) return html`<div class="zui-state">Pick a service.</div>`;
    const prov = this.provider;
    // "Generic"/First-Available provider → empty name so the shared checkout
    // renders its "First available" fallback instead of a placeholder label.
    const providerName = prov && !prov.generic ? prov.name : "";
    const apptWhen = `${WEEKDAYS[this.date.getDay()]}, ${MONTHS[this.date.getMonth()]} ${this.date.getDate()} · ${this.time}`;
    const providerUserId = this.resolvedProviderUserId();
    if (
      this.runtime.surface === "dashboard-customer" &&
      this.runtime.commerce &&
      providerUserId &&
      this.selectedSlot
    ) {
      const offering: CommerceAppointmentOfferingRefDto = {
        kind: CommerceOfferingKind.Appointment,
        offeringKey: `appointment:${svc.id}`,
        providerKey: `provider:${providerUserId}`,
        appointmentTypeKey: svc.id,
      };
      const timeslots: CommerceAppointmentTimeslotSelectionDto[] = [
        {
          timeslotKey: this.selectedSlot.id,
          startsAt: this.selectedSlot.startTime,
          endsAt: this.selectedSlot.endTime,
        },
      ];
      return html`<zipper-commerce-checkout
        .runtime=${this.runtime}
        .offering=${offering}
        .timeslots=${timeslots}
        .requestedAction=${this.bookingAction}
        .orgId=${this.config?.orgId ?? 0}
        .title=${this.bookingAction === CommerceRequestedAction.Buyout
          ? `${svc.name} — private session`
          : svc.name}
        .subtitle=${[apptWhen, providerName || "First available"]
          .filter(Boolean)
          .join(" · ")}
        .stripe=${this.config?.stripe}
        @zipper-commerce-success=${() =>
          this.dispatchEvent(
            new CustomEvent("zipper-appointment-booked", {
              detail: {
                serviceId: svc.id,
                providerId: String(providerUserId),
                time: this.selectedSlot?.startTime ?? "",
              },
              bubbles: true,
              composed: true,
            }),
          )}
      ></zipper-commerce-checkout>`;
    }
    return html` ${this.appointmentBookError
        ? html`<div class="zui-state zui-state--error" role="alert">
            ${this.appointmentBookError}
          </div>`
        : nothing}
      <zipper-booking-checkout
        kind="appointment"
        .serviceName=${svc.name}
        .providerName=${providerName}
        .apptWhen=${apptWhen}
        .duration=${`${svc.duration} min`}
        .price=${this.bookingAction === CommerceRequestedAction.Buyout &&
        typeof svc.buyoutPrice === "number"
          ? svc.buyoutPrice
          : svc.price}
        .image=${svc.thumbnail ?? ""}
        .serviceStripeProductId=${svc.stripeProductId ?? ""}
        .serviceIsFree=${svc.isFree === true}
        .appointmentTypeId=${svc.id}
        .appointmentProviderUserId=${this.resolvedProviderUserId() ?? 0}
        .startIso=${this.selectedSlot?.startTime ?? ""}
        .endIso=${this.selectedSlot?.endTime ?? ""}
        .appointmentCapacity=${svc.allowedParticipants ?? 1}
        .accent=${this.accent}
        .orgId=${this.config?.orgId ?? 0}
        .stripe=${this.config?.stripe}
        .requirePhone=${!!this.opts.requirePhone}
        .requiredSignupFields=${this.config?.requiredSignupFields}
        .currency=${this.config?.currency ?? ""}
        .hideRecipientBooking=${this.hideRecipientBooking}
        .classForms=${serviceForms(svc)}
        @zipper-booking-close=${() => (this.step = 2)}
        @zipper-confirm-book-another=${() => this.bookAnother()}
        .finalizeBooking=${this.finalizeBookingBound}
      ></zipper-booking-checkout>`;
  }

  /**
   * AP-CO: create the real appointment. The shared component charges the card,
   * but its post-charge hook registers a *class* (registerGuest), not an
   * appointment — so the widget books the appointment here from its own
   * provider + slot + service state, via the same anonymous endpoint multisite
   * uses (`appointments-no-auth`). Mock/playground skip it.
   *
   * PLAT-5624: this is wired to the checkout's `finalizeBooking` property, which
   * the checkout AWAITS before rendering its confirmation, and it now REJECTS
   * on failure instead of only painting a banner. Previously it ran off the
   * `zipper-booking-complete` event — fired after `placed` had already flipped —
   * so `Could not find appointment type <uuid>` rendered underneath a
   * "You're booked!" confirmation complete with a receipt line and calendar
   * buttons. Rejecting keeps the checkout on the payment step.
   */
  private async finalizeAppointment(detail?: {
    buyer?: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    intakeSubmissions?: { formId: number; formResponseId: string }[];
    stripeProductId?: string;
    consumeCreditForProductId?: string;
    deferredMembershipPurchaseId?: string | undefined;
    deferredBeneficiaryRosterId?: number | undefined;
    checkoutToken?: string | undefined;
    accessedWithProductId?: string;
    targetUserId?: number;
    currentUserId?: number;
  }): Promise<void> {
    const svc = this.service;
    const puid = this.resolvedProviderUserId();
    // Mock/playground has no backend to book against — resolve so the demo
    // confirmation still renders.
    if (!this.live) return;
    if (!this.config || !svc || puid == null || !this.selectedSlot) {
      // PLAT-5624: live mode with an incomplete selection cannot produce a real
      // appointment, so it must not produce a confirmation either.
      this.appointmentBookError =
        "We lost track of the time slot for this booking — please pick a time again.";
      throw new Error(this.appointmentBookError);
    }
    const intake = detail?.intakeSubmissions ?? [];
    const primary = intake[0];
    const creditProductId = detail?.consumeCreditForProductId;
    this.appointmentBookError = "";
    try {
      await createAppointment(
        this.config.orgId,
        puid,
        {
          appointmentTypeId: svc.id,
          timeSlots: [this.selectedSlot],
          paymentCompleted: !detail?.deferredMembershipPurchaseId,
          deferredMembershipPurchaseId: detail?.deferredMembershipPurchaseId,
          deferredBeneficiaryRosterId: detail?.deferredBeneficiaryRosterId,
          checkoutToken: detail?.checkoutToken,
          consumeCreditForProductId: creditProductId,
          targetUserId: detail?.targetUserId,
          currentUserId: detail?.currentUserId,
          accessedWithProductId:
            detail?.accessedWithProductId || creditProductId,
          // The submission's own form id, not `svc.formId`: the DTO zeroes a
          // legacy `formId` once it migrates the type's forms into `forms[]`,
          // so the type reads 0 on live data (see `serviceForms`) and this used
          // to record `formId: 0` next to a real response id.
          formId: primary?.formId || svc.formId || undefined,
          formResponseId: primary?.formResponseId,
          formSubmissions: intake.length ? intake : undefined,
          overrideStripeProductId:
            detail?.stripeProductId || svc.stripeProductId,
          ...(this.bookingAction === CommerceRequestedAction.Buyout &&
          typeof svc.buyoutPrice === "number"
            ? {
                sessionBuyout: {
                  commerceIntentId: crypto.randomUUID(),
                  priceMinor: Math.round(svc.buyoutPrice * 100),
                },
              }
            : {}),
        },
        ...runtimeApiArguments(this.runtime),
      );
      this.dispatchEvent(
        new CustomEvent("zipper-appointment-booked", {
          detail: {
            serviceId: svc.id,
            providerId: this.providerId,
            time: this.time,
          },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this.appointmentBookError =
        err instanceof Error
          ? err.message
          : "Could not create your appointment — please contact the studio.";
      // PLAT-5624: propagate. The checkout awaits this hook and must NOT render
      // "You're booked!" for an appointment that was never created.
      throw err instanceof Error ? err : new Error(this.appointmentBookError);
    }
  }

  /**
   * AP-CONF: "Book another appointment" on the shared checkout's confirmation
   * resets the widget to its first screen (service selection) for a fresh
   * booking. The shared <zipper-booking-checkout> emits
   * `zipper-confirm-book-another` (bubbling/composed from its internal
   * <zipper-confirmation>), which the checkout screen forwards here. Mirrors how
   * the class flow returns to a clean schedule — the widget stays mounted, so we
   * clear the whole booking selection in place; already-loaded catalog data
   * (services/providers/live availability config) is kept. A brand-new
   * <zipper-booking-checkout> is mounted next time the user reaches step 3, so
   * its own buyer/intake/member state resets with it.
   */
  private bookAnother(): void {
    this.step = 1;
    this.serviceId = "";
    this.serviceOfferingKey = "";
    this.providerId = "any";
    this.time = "";
    this.date = new Date();
    this.weekStart = new Date();
    this.selectedSlot = null;
    this.liveAvail = {};
    this.liveRawSlots = [];
    this.dayCounts = {};
    this.loadedDays = new Set();
    this.expandedServiceIds = new Set();
    this.collapsedCats = new Set();
    // PLAT-5624: also drop the group drill-down, so "Book another appointment"
    // lands on the top-level group list rather than deep inside the last
    // group/provider the previous booking went through.
    this.groupTitle = "";
    this.groupProviderId = "";
    this.bookingAction = CommerceRequestedAction.Book;
    this.clearSharedChoice();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-appointments": ZipperAppointments;
  }
}
